import { useEffect, useState, type ReactNode } from 'react';
import {
  answerFeedback,
  assignFeedback,
  categorizeFeedback,
  categoryCatalog,
  closeFeedback,
  escalateFeedback,
  feedbackNotifications,
  type CategoryCatalog,
  type FeedbackItem,
  type NotificationItem,
} from '../api';
import { type FeedbackLocale, type FeedbackTranslate } from '../i18n';
import {
  Button,
  Card,
  Field,
  Id,
  Notice,
  Select,
  StatusBadge,
  TextInput,
  Moment,
} from '../../../shell/kit';
import { EmployeePicker, useEmployeeNames } from '../../../shell/pickers';
import { useToast } from '../../../shell/toast';
import { useCategoryCatalog } from '../catalog';
import { errorText } from '../../../shell/errors';

export function FeedbackCard({
  item,
  t,
  locale,
  canAnswer,
  siteId,
  onChanged,
  onClose,
}: {
  item: FeedbackItem;
  t: FeedbackTranslate;
  locale: FeedbackLocale;
  canAnswer: boolean;
  siteId: string | null;
  onChanged: (next: FeedbackItem) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const { categoryName } = useCategoryCatalog();
  const [catalog, setCatalog] = useState<CategoryCatalog | null>(null);
  const [assignee, setAssignee] = useState('');
  const employeeNames = useEmployeeNames(siteId);

  /** Имя сотрудника, а идентификатор — рядом мелким, чтобы его можно было скопировать. */
  const who = (id: string | null): ReactNode => {
    if (!id) return '—';
    const name = employeeNames.get(id);
    return name ? (
      <>
        {name} <Id value={id} />
      </>
    ) : (
      <Id value={id} />
    );
  };
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState(item.category ?? '');
  const [reason, setReason] = useState(item.reasonCode ?? '');
  const [resolution, setResolution] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void categoryCatalog().then(setCatalog).catch(() => setCatalog(null));
  }, []);

  /*
   * Что отправлено по обращению: ответ клиенту и уведомления руководителю об
   * эскалации. Без этого «эскалация» в карточке не отвечала на главный вопрос —
   * узнал ли о ней кто-нибудь.
   */
  const [notifications, setNotifications] = useState<readonly NotificationItem[] | null>(null);
  useEffect(() => {
    let alive = true;
    void feedbackNotifications(item.id)
      .then((page) => alive && setNotifications(page.items))
      .catch(() => alive && setNotifications(null));
    return () => {
      alive = false;
    };
  }, [item.id, item.status]);

  useEffect(() => {
    setCategory(item.category ?? '');
    setReason(item.reasonCode ?? '');
  }, [item.id, item.category, item.reasonCode]);

  const act = async (fn: () => Promise<FeedbackItem>): Promise<void> => {
    setBusy(true);
    try {
      onChanged(await fn());
      toast.ok(t('feedback.common.saved'));
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Переходы берутся с сервера. Раньше кнопка «Закрыть» разблокировалась только
   * по подтверждённости категории и не сверялась с таблицей переходов: из статуса
   * «Эскалировано» она была активна, а сервер отвечал 409 — закрыть можно только
   * из «Отвечено».
   */
  const transitions = item.allowedTransitions ?? [];
  const knowsTransitions = transitions.length > 0;
  const canClose =
    item.categorySource === 'confirmed' && (!knowsTransitions || transitions.includes('closed'));
  const canEscalate = !knowsTransitions || transitions.includes('escalated');
  const canAssign = !knowsTransitions || transitions.includes('assigned');
  const canAnswerNow = !knowsTransitions || transitions.includes('answered');

  const closeBlockedReason =
    item.categorySource !== 'confirmed'
      ? t('feedback.category.mustConfirm')
      : knowsTransitions && !transitions.includes('closed')
        ? t('feedback.action.closeBlocked', { status: t(`feedback.status.${item.status}` as never) })
        : null;

  const reasons = (catalog?.reasons ?? []).filter((r) => r.categoryCode === category);

  return (
    <Card title={t('feedback.card.title')}>

      <dl className="my-3 space-y-1.5 text-sm">
        <Row
          label={t('feedback.queue.status')}
          value={
            <StatusBadge
              tone={
                item.status === 'closed'
                  ? 'ok'
                  : item.status === 'escalated'
                    ? 'danger'
                    : item.overdue
                      ? 'warn'
                      : 'neutral'
              }
            >
              {t(`feedback.status.${item.status}` as never)}
            </StatusBadge>
          }
        />
        <Row label={t('feedback.queue.channel')} value={t(`feedback.channel.${item.channel}` as never)} />
        <Row label={t('feedback.queue.score')} value={item.score === null ? '—' : `${item.score}/5`} />
        <Row label={t('feedback.queue.created')} value={<Moment iso={item.createdAt} />} />
        <Row label={t('feedback.queue.due')} value={<Moment iso={item.dueAt} />} />
        <Row
          label={t('feedback.card.visit')}
          value={
            item.visitId ? (
              <Id value={item.visitId} />
            ) : (
              <span className="text-warn">{t('feedback.card.noVisit')}</span>
            )
          }
        />
        {/*
          * Обе строки показывали короткий идентификатор. Вопрос, на который они
          * отвечают, — «кто это»; идентификатор на него не отвечает.
          */}
        <Row label={t('feedback.card.employee')} value={who(item.employeeId)} />
        <Row label={t('feedback.card.assignee')} value={who(item.assigneeId)} />
        <Row
          label={t('feedback.card.text')}
          value={item.body ?? <span className="text-ink-faint">{t('feedback.card.noText')}</span>}
        />
        {item.resolution ? <Row label={t('feedback.card.resolution')} value={item.resolution} /> : null}
        {item.contactPhone ? <Row label={t('feedback.card.contact')} value={item.contactPhone} /> : null}
      </dl>

      {item.escalations && item.escalations.length > 0 ? (
        <section className="mb-3 rounded border border-danger-line bg-danger-bg p-3 text-sm">
          <p className="mb-1 font-medium text-danger">{t('feedback.card.escalations')}</p>
          <ul className="space-y-1">
            {item.escalations.map((e) => (
              <li key={e.id} className="flex flex-wrap gap-x-2 text-ink">
                <Moment iso={e.createdAt} />
                <span>{t(`feedback.esc.reason.${e.reason}` as never)}</span>
                <span className="text-ink-muted">→ {e.toEmployeeName ?? t('feedback.esc.nobody')}</span>
                {e.reason === 'manual' && e.note ? <span className="text-ink-muted">«{e.note}»</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {notifications ? (
        <section className="mb-3 rounded border border-line p-3 text-sm">
          <p className="mb-1 font-medium text-ink">{t('feedback.card.notifications')}</p>
          {notifications.length === 0 ? (
            <p className="text-xs text-ink-muted">{t('feedback.card.notificationsEmpty')}</p>
          ) : (
            <ul className="space-y-1">
              {notifications.map((n) => (
                <li key={n.id} className="flex flex-wrap items-center gap-x-2">
                  <Moment iso={n.sentAt ?? n.createdAt} />
                  <span>{n.employeeName ?? n.customerName ?? n.recipientMasked}</span>
                  <span className="text-xs text-ink-muted">{n.channel}</span>
                  <StatusBadge tone={n.status === 'sent' || n.status === 'delivered' ? 'ok' : n.status === 'failed' || n.status === 'suppressed' ? 'danger' : 'neutral'}>
                    {t(`feedback.msg.${n.status}` as never)}
                  </StatusBadge>
                  {n.suppressedReason || n.lastError ? (
                    <span className="text-xs text-danger">{n.suppressedReason ?? n.lastError}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {item.categorySource === 'suggested' ? (
        <Notice tone="warn">{t('feedback.category.suggestedHint')}</Notice>
      ) : null}

      {canAnswer ? (
        <div className="mt-4 space-y-4">
          <section className="rounded border border-line p-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-64 flex-1">
                <Field label={t('feedback.action.assignee')}>
                  <EmployeePicker
                    value={assignee}
                    onChange={setAssignee}
                    siteId={siteId}
                    emptyLabel={t('feedback.action.assigneeEmpty')}
                  />
                </Field>
              </div>
              <Button
                disabled={busy || !assignee || !canAssign}
                onClick={() => void act(() => assignFeedback(item.id, assignee.trim()))}
              >
                {t('feedback.action.assign')}
              </Button>
            </div>
          </section>

          <section className="rounded border border-line p-3">
            <Field label={t('feedback.action.answerText')} hint={t('feedback.action.answerHint')}>
              <TextInput value={answer} onChange={(e) => setAnswer(e.target.value)} />
            </Field>
            <div className="mt-2">
              <Button
                tone="primary"
                disabled={busy || answer.trim().length === 0 || !canAnswerNow}
                onClick={() => void act(() => answerFeedback(item.id, answer.trim()))}
              >
                {t('feedback.action.answer')}
              </Button>
            </div>
          </section>

          <section className="rounded border border-line p-3">
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t('feedback.action.category')}>
                <Select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setReason('');
                  }}
                >
                  <option value="">—</option>
                  {(catalog?.categories ?? []).map((c) => (
                    <option key={c.code} value={c.code}>
                      {locale === 'kk' ? c.nameKk : c.nameRu}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('feedback.action.reason')}>
                <Select value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="">{t('feedback.action.noReason')}</option>
                  {reasons.map((r) => (
                    <option key={r.code} value={r.code}>
                      {locale === 'kk' ? r.nameKk : r.nameRu}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button
                disabled={busy || !category}
                onClick={() =>
                  void act(() => categorizeFeedback(item.id, category, reason === '' ? null : reason))
                }
              >
                {t('feedback.action.categorize')}
              </Button>
            </div>
          </section>

          <section className="rounded border border-line p-3">
            {closeBlockedReason ? <Notice tone="info">{closeBlockedReason}</Notice> : null}
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <div className="min-w-64 flex-1">
                <Field label={t('feedback.action.resolution')}>
                  <TextInput value={resolution} onChange={(e) => setResolution(e.target.value)} />
                </Field>
              </div>
              <Button
                tone="primary"
                disabled={busy || !canClose}
                onClick={() => void act(() => closeFeedback(item.id, resolution))}
              >
                {t('feedback.action.closeIt')}
              </Button>
            </div>
          </section>

          <section className="rounded border border-line p-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-64 flex-1">
                <Field label={t('feedback.action.note')}>
                  <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
                </Field>
              </div>
              <Button
                tone="danger"
                disabled={busy || note.trim().length === 0 || !canEscalate}
                onClick={() => void act(() => escalateFeedback(item.id, note.trim()))}
              >
                {t('feedback.action.escalate')}
              </Button>
            </div>
          </section>
        </div>
      ) : null}

      <div className="mt-4">
        <Button onClick={onClose}>{t('feedback.card.close')}</Button>
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-44 shrink-0 text-ink-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}
